'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';

const CATEGORIES = [
  { id: 'all',           label: 'All Questions',    emoji: '📋' },
  { id: 'general',       label: 'Getting Started',  emoji: '🚀' },
  { id: 'legal',         label: 'Legal & Compliance', emoji: '⚖️' },
  { id: 'delivery',      label: 'Delivery & Timing', emoji: '⚡' },
  { id: 'visa-specific', label: 'Visa Applications', emoji: '✈️' },
  { id: 'pricing',       label: 'Pricing & Refunds', emoji: '💳' },
];

function Accordion({ items }) {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash || !items.some((item) => item.id === hash)) return;

    setOpenId(hash);
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [items]);

  if (!items.length) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <HelpCircle className="mb-3 h-10 w-10 text-slate-200" strokeWidth={1.5} />
        <p className="text-sm text-slate-400">No questions in this category yet.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {items.map((item, index) => {
        const isOpen = openId === item.id;
        const n = String(index + 1).padStart(2, '0');
        return (
          <div key={item.id} id={item.id} className="group scroll-mt-28">
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : item.id)}
              aria-expanded={isOpen}
              className="flex w-full items-start justify-between gap-4 py-5 text-left outline-none md:gap-5"
            >
              <span className="flex min-w-0 flex-1 items-start gap-3">
                <span
                  className={`mt-0.5 flex h-7 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold tabular-nums transition-colors md:h-8 md:w-9 md:text-xs ${
                    isOpen
                      ? 'bg-primary/15 text-primary ring-1 ring-primary/20'
                      : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200 group-hover:bg-primary/10 group-hover:text-primary'
                  }`}
                  aria-hidden
                >
                  {n}
                </span>
                <span className={`min-w-0 text-[15px] font-semibold leading-snug transition-colors ${
                  isOpen ? 'text-primary' : 'text-slate-800 group-hover:text-primary'
                }`}>
                  {item.question}
                </span>
              </span>
              <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all ${
                isOpen
                  ? 'bg-primary/10 ring-1 ring-primary/20'
                  : 'bg-slate-100 ring-1 ring-slate-200 group-hover:bg-primary/10'
              }`}>
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${
                    isOpen ? 'rotate-180 text-primary' : 'text-slate-400'
                  }`}
                  strokeWidth={2.5}
                />
              </span>
            </button>

            <div
              className="grid transition-all duration-300 ease-in-out"
              style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <p className="pb-5 pr-8 text-sm leading-relaxed text-slate-500">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FaqPageClient({ faqs }) {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? faqs
    : faqs.filter((f) => f.category === activeCategory);

  const countFor = (id) =>
    id === 'all' ? faqs.length : faqs.filter((f) => f.category === id).length;

  return (
    <main className="min-h-screen w-full bg-slate-50">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden border-b border-slate-100 bg-white py-14 md:py-20">
        {/* Background blobs */}
        <div className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-0 h-[300px] w-[300px] rounded-full bg-secondary/5 blur-3xl" />

        <div className="custom-mx relative z-10 mx-auto max-w-7xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/5 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
            <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
            Frequently Asked Questions
          </div>

          <h1 className="text-[2rem] font-bold leading-[1.15] tracking-normal text-slate-900 sm:text-[2.5rem] md:text-[3rem]">
            Got Questions?{' '}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              We&apos;ve Got Answers
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-500">
            Everything you need to know about dummy tickets, visa reservations,
            our Standard GDS-format documents, and delivery — from getting started to edge cases.
          </p>

          {/* Stats */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
            {[
              { value: `${faqs.length}+`, label: 'Questions answered' },
              { value: '5', label: 'Categories' },
              { value: '2–4 hr', label: 'Support response' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <p className="text-xl font-bold text-primary">{value}</p>
                <p className="text-xs text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="custom-mx mx-auto max-w-7xl py-12 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr] lg:items-start lg:gap-10">

          {/* ── Left: sticky sidebar ─────────────────────────── */}
          <aside className="lg:sticky lg:top-[7.5rem]">

            {/* Category nav */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Browse by Category
                </p>
              </div>

              <nav className="p-2">
                {CATEGORIES.map(({ id, label, emoji }) => {
                  const count = countFor(id);
                  if (count === 0 && id !== 'all') return null;
                  const isActive = activeCategory === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveCategory(id)}
                      className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition-all ${
                        isActive
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="text-base leading-none">{emoji}</span>
                        <span className={`text-sm font-semibold ${isActive ? 'text-white' : ''}`}>
                          {label}
                        </span>
                      </span>
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                        isActive
                          ? 'bg-white/20 text-white'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Still have questions CTA */}
            <div className="mt-4 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5 p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <MessageCircle className="h-5 w-5" strokeWidth={2.25} />
              </div>
              <p className="text-sm font-semibold text-slate-800">Still have questions?</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Can&apos;t find what you&apos;re looking for? Our support team is here to help.
              </p>
              <Link
                href="/support"
                className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-[linear-gradient(315deg,#288ac4_0%,#16325c_100%)] px-4 py-2.5 text-xs font-bold text-white transition hover:opacity-90"
              >
                Contact Support
              </Link>
            </div>

          </aside>

          {/* ── Right: FAQ accordion ─────────────────────────── */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

            {/* Section header */}
            <div className="border-b border-slate-100 px-6 py-5 md:px-7">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {CATEGORIES.find((c) => c.id === activeCategory)?.label}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {filtered.length} question{filtered.length !== 1 ? 's' : ''}
                  </p>
                </div>
                {activeCategory !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setActiveCategory('all')}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    View all →
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 md:px-7">
              <Accordion items={filtered} />
            </div>

          </div>

        </div>
      </div>

    </main>
  );
}
